import { useMemo, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import type { AccountDossierTheme, AccountFact } from '@/lib/account-knowledge';
import { cn } from '@/lib/utils';
import { AccountFactRow } from './AccountFactRow';

/**
 * The short text of an account, by theme. Each sentence carries small numbers
 * that open its facts in place (quote, source, « × »). A sentence whose facts
 * were all removed fades until the text is rewritten without them. Only the
 * first theme — what is at stake — is open; the others open on their title
 * (M2, 30/10: a rich account's history makes a long text).
 */
export const AccountDossierText = ({
  dossier,
  facts,
  accountSubjectId,
  nameOf,
  onRemove,
}: {
  dossier: AccountDossierTheme[];
  facts: Map<string, AccountFact>;
  accountSubjectId: string;
  nameOf: (userId: string) => string | null;
  onRemove: (fact: AccountFact) => void;
}) => {
  const [openFact, setOpenFact] = useState<string | null>(null);
  const [openThemes, setOpenThemes] = useState<Set<string>>(() => new Set());
  const toggleTheme = (theme: string) => setOpenThemes((current) => {
    const next = new Set(current);
    if (!next.delete(theme)) next.add(theme);
    return next;
  });
  // Numbers follow the first time a fact is cited in the text.
  const numbers = useMemo(() => {
    const order = new Map<string, number>();
    dossier.forEach((theme) => theme.sentences.forEach((sentence) => sentence.factIds.forEach((id) => {
      if (!order.has(id)) order.set(id, order.size + 1);
    })));
    return order;
  }, [dossier]);

  return (
    <div className="space-y-4 px-5 py-4 sm:px-6">
      {dossier.map((theme, themeIndex) => {
        const opened = openFact && theme.sentences.some((sentence) => sentence.factIds.includes(openFact)) ? facts.get(openFact) : null;
        const first = themeIndex === 0;
        const expanded = first || openThemes.has(theme.theme);
        const title = 'text-[11px] font-semibold uppercase tracking-[0.08em] text-domino-inkSoft';
        return (
          <div key={theme.theme}>
            {first ? <p className={title}>{theme.theme}</p> : (
              <button
                type="button"
                aria-expanded={expanded}
                onClick={() => toggleTheme(theme.theme)}
                className={cn(title, 'group inline-flex items-center gap-1 rounded transition-colors duration-150 hover:text-domino-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-domino-prune')}
              >
                <ChevronRight aria-hidden className={cn('h-3 w-3 transition-transform duration-150 ease-snap', expanded && 'rotate-90')} />
                {theme.theme}
              </button>
            )}
            {expanded && (<>
            <p className="mt-1 text-sm leading-6 text-domino-ink motion-safe:animate-fadeIn">
              {theme.sentences.map((sentence, index) => {
                const gone = sentence.factIds.every((id) => facts.get(id)?.state !== 'active');
                return (
                  <span key={`${index}-${sentence.text}`} className={cn('transition-opacity duration-200', gone && 'text-domino-inkSoft line-through opacity-50')}>
                    {sentence.text}
                    {sentence.factIds.map((id) => facts.get(id) ? (
                      <button
                        key={id}
                        type="button"
                        aria-expanded={openFact === id}
                        aria-label={`Voir le fait ${numbers.get(id)}`}
                        onClick={() => setOpenFact((current) => current === id ? null : id)}
                        className={cn(
                          'ml-0.5 inline-flex h-4 min-w-4 -translate-y-1 items-center justify-center rounded px-1 align-baseline text-[10px] font-semibold leading-none transition-[background-color,transform] duration-150 ease-snap active:scale-[0.92] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-domino-prune',
                          openFact === id ? 'bg-domino-aubergine text-white' : 'bg-domino-plumTint text-domino-aubergine hover:bg-domino-lilac',
                        )}
                      >
                        {numbers.get(id)}
                      </button>
                    ) : null)}
                    {index < theme.sentences.length - 1 ? ' ' : ''}
                  </span>
                );
              })}
            </p>
            {opened && (
              <ul className="-mx-5 mt-2 rounded-xl bg-domino-muted/60 motion-safe:animate-fadeIn sm:-mx-6">
                <AccountFactRow fact={opened} accountSubjectId={accountSubjectId} nameOf={nameOf} open
                  before={opened.replacesFactId ? facts.get(opened.replacesFactId) ?? null : null}
                  onRemove={(fact) => { setOpenFact(null); onRemove(fact); }} />
              </ul>
            )}
            </>)}
          </div>
        );
      })}
    </div>
  );
};
